import { Modal } from "tailwindcss-stimulus-components"

export default class extends Modal {
  static targets = ["dialog", "frame", "closeButton"]

  connect() {
    super.connect()
    document.addEventListener('turbo:frame-load', this.handleFrameLoad)
    document.addEventListener('turbo:before-stream-render', this.handleBeforeStreamRender)
    document.addEventListener('turbo:load', this.handleTurboLoad)
    document.addEventListener('click', this.handleModalLinkClick)

    // Listen for native dialog close event (triggered by Escape key)
    if (this.hasDialogTarget) {
      this.dialogTarget.addEventListener('close', this.handleNativeClose)
      this.dialogTarget.addEventListener('submit', this.handleDialogFormSubmit)

      // Ensure the frame starts clean on page load (handles Turbo Drive cache)
      const frame = this.dialogTarget.querySelector('#content_modal_frame')
      if (frame && !this.dialogTarget.open) {
        frame.innerHTML = ''
        frame.removeAttribute('src')
      }
    }
  }

  disconnect() {
    document.removeEventListener('turbo:frame-load', this.handleFrameLoad)
    document.removeEventListener('turbo:before-stream-render', this.handleBeforeStreamRender)
    document.removeEventListener('turbo:load', this.handleTurboLoad)
    document.removeEventListener('click', this.handleModalLinkClick)

    if (this.hasDialogTarget) {
      this.dialogTarget.removeEventListener('close', this.handleNativeClose)
      this.dialogTarget.removeEventListener('submit', this.handleDialogFormSubmit)
    }

    super.disconnect()
  }

  handleNativeClose = (event) => {
    this.performCleanup()
  }

  // Prevent Lexxy's <form method="dialog"> from closing our modal dialog.
  // Lexxy's link dropdown uses method="dialog" which natively closes the nearest
  // ancestor <dialog> element - in our case, the content modal.
  handleDialogFormSubmit = (event) => {
    const form = event.target
    if (form.method === 'dialog' && form.closest('lexxy-link-dropdown')) {
      event.preventDefault()
    }
  }

  handleTurboLoad = () => {
    this.openFromUrlParams()
  }

  handleFrameLoad = (event) => {
    // Only show modal if the loaded frame is the one inside the dialog (not a page-level frame with the same ID)
    if (event.target.id === 'content_modal_frame' &&
      event.target.innerHTML.trim().length > 0 &&
      this.hasDialogTarget &&
      this.dialogTarget.contains(event.target)) {
      this.open()
    }
  }

  handleModalLinkClick = (event) => {
    // Ignore modifier clicks that open in a new tab/window
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if (event.button && event.button !== 0) return
    if (event.defaultPrevented) return

    const link = event.target.closest('[data-turbo-frame="content_modal_frame"]')
    if (!link) return
    if (!this.hasDialogTarget) return

    // Only intercept if the link will actually load something
    const href = link.getAttribute('href') || link.getAttribute('action')
    if (!href || href === '#') return

    const frame = this.hasFrameTarget
      ? this.frameTarget
      : document.getElementById('content_modal_frame')
    if (!frame) return

    // A link that already lives INSIDE the frame is left alone: it navigates
    // that frame by default, so it needs no interception here. Intercepting it
    // is actively harmful -- the skeleton swap below calls
    // frame.replaceChildren(), which destroys the very link being clicked, and
    // Turbo then loses its frame context and full-page navigates out of the
    // modal (the rated images gallery taking over the viewport when opened
    // from inside a modal, and the same latent flaw in
    // project_workflows/show.html.erb and shareables/image.html.erb).
    //
    // Deliberately narrow: only a descendant of the frame, not anything that
    // merely looks internal. Links OUTSIDE the frame keep the existing
    // skeleton-then-open behaviour unchanged -- that is the normal path.
    if (frame.contains(link)) return

    const template = document.getElementById('content_modal_skeleton_template')
    if (template && 'content' in template) {
      frame.replaceChildren(template.content.cloneNode(true))
    }

    this.open()
  }

  handleBeforeStreamRender = (event) => {
    // Close modal when turbo stream clears the content_modal_frame
    const streamAction = event.detail.newStream
    if (streamAction && streamAction.target === 'content_modal_frame') {
      const actionType = streamAction.action
      if (actionType === 'update' || actionType === 'replace') {
        const content = streamAction.templateContent?.textContent?.trim()
        if (!content || content === '') {
          if (this.hasDialogTarget && this.dialogTarget.open) {
            this.close()
          }
        }
      }
    }
  }

  open(event) {
    // Idempotent: showModal() throws InvalidStateError if the dialog is already open.
    if (this.hasDialogTarget && this.dialogTarget.open) return

    this.previouslyFocusedElement = document.activeElement

    super.open(event)

    // Show the close button now that modal is open
    if (this.hasCloseButtonTarget) {
      this.closeButtonTarget.classList.remove('hidden')
    }

    // Hide floating activity feed if present (prevents duplicate activities_list IDs)
    const floatingFeed = document.querySelector('[data-controller~="activity-feed"]')
    if (floatingFeed) {
      floatingFeed.style.display = 'none'
    }

    // Hide the page-behind's docked activity feed, if it has one (e.g. a
    // Project board's own feed). Only the feed(s) OUTSIDE this dialog are
    // hidden -- a docked feed loaded into the modal's own frame (or the
    // page-level content_modal_frame on a Card's own show/edit page) stays
    // visible. IDs aren't unique here (the modal frame can load its own
    // "docked-activity-feed-panel"), so query all matches rather than
    // getElementById. Hide the outer collapsible-panel/bottom-sheet wrapper
    // rather than just the inner panel -- on mobile the wrapper also holds
    // the floating "Activity" trigger button and backdrop, which would
    // otherwise stay visible over the modal. Hiding the wrapper only toggles
    // CSS display; the collapsible-panel/bottom-sheet controllers stay
    // connected and keep their own state.
    this.hiddenDockedFeedWrappers = Array.from(new Set(
      Array.from(document.querySelectorAll('#docked-activity-feed-panel'))
        .map((panel) => panel.closest('[data-controller~="collapsible-panel"]') || panel)
    )).filter((wrapper) => !this.hasDialogTarget || !this.dialogTarget.contains(wrapper))
    this.hiddenDockedFeedWrappers.forEach((wrapper) => {
      wrapper.style.display = 'none'
    })

    // Lock body scroll -- native <dialog> showModal() does not do this on its own,
    // so the page behind the modal keeps scrolling with it.
    document.body.style.overflow = 'hidden'
  }

  close(event) {
    if (event && event.preventDefault) {
      event.preventDefault()
    }

    // Release the scroll lock synchronously here rather than waiting on
    // performCleanup() alone -- the base Modal#close() defers the actual
    // native dialog.close() (and therefore the 'close' event that triggers
    // performCleanup) until any closing-animation promise resolves, which
    // is a real, observable race against code that checks body.style
    // immediately after triggering a close.
    document.body.style.overflow = ''

    super.close(event)
  }

  performCleanup() {
    // Hide the close button when modal closes
    if (this.hasCloseButtonTarget) {
      this.closeButtonTarget.classList.add('hidden')
    }

    // Clear the turbo frame inside the dialog only (not page-level frames with the same ID)
    if (this.hasDialogTarget) {
      const frame = this.dialogTarget.querySelector('#content_modal_frame')
      if (frame) {
        frame.innerHTML = ''
        frame.removeAttribute('src')
      }
    }

    // Restore floating activity feed if present
    const floatingFeed = document.querySelector('[data-controller~="activity-feed"]')
    if (floatingFeed) {
      floatingFeed.style.display = ''
    }

    // Restore any docked activity feed wrapper(s) hidden on open
    if (this.hiddenDockedFeedWrappers) {
      this.hiddenDockedFeedWrappers.forEach((wrapper) => {
        wrapper.style.display = ''
      })
      this.hiddenDockedFeedWrappers = null
    }

    // Restore body scroll -- runs on every close path (X button, Escape,
    // backdrop click, frame-clear) since they all funnel through performCleanup().
    document.body.style.overflow = ''

    // Restore focus to the element that was focused before modal opened
    if (this.previouslyFocusedElement && this.previouslyFocusedElement.focus) {
      const elementToFocus = this.previouslyFocusedElement
      this.previouslyFocusedElement = null
      setTimeout(() => {
        if (elementToFocus && elementToFocus.focus) {
          elementToFocus.focus()
        }
      }, 0)
    }
  }

  hide(event) {
    this.close(event)
  }

  openFromUrlParams() {
    const params = new URLSearchParams(window.location.search)
    const cardId = params.get('open_card')
    const editCardId = params.get('edit_card')
    const objectiveId = params.get('open_objective')
    const editObjectiveId = params.get('edit_objective')

    const projectMatch = window.location.pathname.match(/\/projects\/(\d+)/)
    if (!projectMatch) return

    const projectId = projectMatch[1]
    let url = null
    let paramToRemove = null

    if (editCardId) {
      url = `/projects/${projectId}/cards/${editCardId}/edit`
      paramToRemove = 'edit_card'
    } else if (cardId) {
      url = `/projects/${projectId}/cards/${cardId}`
      paramToRemove = 'open_card'
    } else if (editObjectiveId) {
      url = `/projects/${projectId}/objectives/${editObjectiveId}/edit`
      paramToRemove = 'edit_objective'
    } else if (objectiveId) {
      url = `/projects/${projectId}/objectives/${objectiveId}`
      paramToRemove = 'open_objective'
    }

    if (url && this.hasDialogTarget) {
      const frame = this.dialogTarget.querySelector('#content_modal_frame')
      if (frame) {
        frame.src = url
      }
      this.open()

      // Clean up URL so refreshing doesn't re-open
      const cleanUrl = new URL(window.location)
      cleanUrl.searchParams.delete(paramToRemove)
      history.replaceState({}, '', cleanUrl)
    }
  }

  openCard(event) {
    // Don't open modal if clicking on interactive elements
    const target = event.target
    const clickedButton = target.closest('button')
    const clickedLink = target.closest('a')
    const clickedInput = target.closest('input, textarea')

    // Check if we're in edit mode for inline editing (input is visible)
    const inlineEditDiv = target.closest('[data-controller="inline-edit"]')
    const inEditMode = inlineEditDiv && inlineEditDiv.querySelector('[data-inline-edit-target="input"]:not(.hidden)')

    // Check if clicking on expandable content areas
    const clickedExpandable = target.closest('[data-expandable-target="content"]')

    if (clickedButton || clickedLink || clickedInput || inEditMode || clickedExpandable) {
      return
    }

    event.preventDefault()

    const cardElement = event.currentTarget
    const cardId = cardElement.dataset.cardId
    const projectId = cardElement.dataset.projectId

    if (!cardId || !projectId) return

    // Load card preview into modal (use dialog's frame specifically)
    const frame = this.hasDialogTarget
      ? this.dialogTarget.querySelector('#content_modal_frame')
      : document.getElementById('content_modal_frame')
    if (frame) {
      const template = document.getElementById('content_modal_skeleton_template')
      if (template && 'content' in template) {
        frame.replaceChildren(template.content.cloneNode(true))
      }
      frame.src = `/projects/${projectId}/cards/${cardId}`
    }

    this.open()
  }
}
